import { TEAM } from '../MatchAuthority.js';

/**
 * Search & Destroy — rounds, one life each, a bomb.
 *
 * The mode that breaks the most assumptions the rest of the game was built on,
 * which is exactly why it is worth having: every one of them was an assumption
 * worth naming.
 *
 *   - **You do not respawn.** `GameDirector` has always put the player back on a
 *     timer. It now asks `allowRespawn` first, and this is the one mode that says
 *     no. Death costing the whole round is the entire premise; a three-second
 *     respawn would turn it into Team Deathmatch with extra steps.
 *   - **The match is a sequence, not a state.** Everyone comes back at the start of
 *     a round, so the director gets a `resetRound` that clears the field — corpses
 *     included, because a round opening on the last one's bodies reads as a bug.
 *   - **Sides swap.** You play half the rounds attacking. A mode where one side is
 *     stronger is a mode you would rather always be on one side of, so both sides
 *     have to be played to make the score mean anything.
 *
 * ## Planting by standing there
 *
 * There is no plant button. An attacker inside a site plants; a defender inside a
 * planted site defuses. Both progress whether or not the other team is present —
 * that contest *is* the round.
 *
 * This is a real design choice and not a shortcut around a keybinding. It makes the
 * bots play the objective with no AI work at all, because "is a soldier of the
 * attacking team standing in the circle" is a question the mode can already answer
 * about anybody. A plant key would have had to be taught to the AI as a separate
 * behaviour, and taught to a touch overlay as a twelfth button on a screen that is
 * already crowded. Standing somewhere dangerous for eight seconds is the commitment
 * the mechanic is actually made of; the button was never the interesting part.
 */

export const TUNING = {
  sites: 2,
  radius: 5.5,
  height: 3.2,
  plantTime: 8.0,
  defuseTime: 10.0,
  /** Seconds on the bomb once planted. Long enough to fight over, short enough to lose. */
  fuse: 45,
  roundTime: 110,
  /** Rounds one side must win to take the match. Six played means three each way. */
  roundsToWin: 4,
  /** Seconds between the round ending and the next one starting. */
  intermission: 5.0,
  squad: 5,
};

const LETTERS = ['A', 'B', 'C'];

export class SearchDestroy {
  constructor(opts = {}) {
    this.id = 'snd';
    this.name = 'サーチ＆デストロイ';
    this.ownsVictory = true;
    /** Friendly fire on, as the mode has always been played: one life makes it matter. */
    this.friendlyFire = opts.friendlyFire ?? true;
    this.selfDamage = true;

    this.roundsToWin = opts.roundsToWin ?? TUNING.roundsToWin;
    this.squad = opts.squad ?? TUNING.squad;

    this.score = { [TEAM.ALPHA]: 0, [TEAM.BRAVO]: 0 };
    /** Who is planting this round. Swaps at the halfway point. */
    this.attackers = TEAM.ALPHA;
    this.round = 1;
    this.phase = 'live'; // 'live' | 'over'
    this.roundClock = TUNING.roundTime;
    this.intermission = 0;
    this.bomb = null; // { site, timer } once planted
    this.sites = [];
    this.elapsed = 0;
    this._lastResult = null;
  }

  attach(match) {
    this.match = match;
    this.events = match.events;
    this._publish();
  }

  get roster() {
    return { [TEAM.ALPHA]: Math.max(0, this.squad - 1), [TEAM.BRAVO]: this.squad };
  }

  canDamage(src, dst) {
    if (this.friendlyFire) return true;
    if (src.team === TEAM.NONE || dst.team === TEAM.NONE) return true;
    return src.team !== dst.team;
  }

  canSplash() {
    return true;
  }

  onKill() {
    // Kills score nothing directly; they win rounds by removing a team.
  }

  /** The one mode that refuses. See the note at the top of the file. */
  allowRespawn() {
    return false;
  }

  get defenders() {
    return this.attackers === TEAM.ALPHA ? TEAM.BRAVO : TEAM.ALPHA;
  }

  /* -------------------------------- sites --------------------------------- */

  _placeSites(ctx) {
    const pool = ctx.ai?.spawnPoints;
    if (!pool?.length) return false;
    // The two most distant reachable points, same derivation as Domination's
    // flags — for the same reason: nothing here should be a typed-in coordinate.
    let a = pool[0];
    let b = pool[0];
    let bestD = -1;
    for (let i = 0; i < pool.length; i++) {
      for (let j = i + 1; j < pool.length; j++) {
        const d = pool[i].distanceToSquared(pool[j]);
        if (d > bestD) {
          bestD = d;
          a = pool[i];
          b = pool[j];
        }
      }
    }
    this.sites = [a, b].slice(0, TUNING.sites).map((p, i) => ({
      id: LETTERS[i],
      position: p.clone(),
      progress: 0,
      planted: false,
      actors: { [TEAM.ALPHA]: 0, [TEAM.BRAVO]: 0 },
    }));
    this._publishZones();
    return true;
  }

  _countInside(site) {
    const n = site.actors;
    n[TEAM.ALPHA] = 0;
    n[TEAM.BRAVO] = 0;
    const r2 = TUNING.radius * TUNING.radius;
    for (const reg of this.match.actors.values()) {
      if (reg.dead || typeof reg.actor.health !== 'number') continue;
      const p = reg.actor.position;
      if (!p) continue;
      const dy = p.y - site.position.y;
      if (dy < -TUNING.height || dy > TUNING.height) continue;
      const dx = p.x - site.position.x;
      const dz = p.z - site.position.z;
      if (dx * dx + dz * dz > r2) continue;
      if (reg.team === TEAM.ALPHA || reg.team === TEAM.BRAVO) n[reg.team]++;
    }
    return n;
  }

  /* ------------------------------ rally points ---------------------------- */

  /**
   * Attackers walk to a site; defenders hold one. Once the bomb is down, both
   * sides want the same circle, which is the shape the endgame should have.
   */
  rallyFor(team, actor) {
    if (!this.sites.length || this.phase !== 'live') return null;
    if (this.bomb) return this.bomb.site.position;
    // Split the squad across the sites deterministically rather than randomly, so
    // a team does not all pick the same one and leave the other unwatched.
    const i = Math.abs(hashId(actor?.id ?? '')) % this.sites.length;
    return this.sites[i].position;
  }

  /* --------------------------------- tick --------------------------------- */

  tick(dt, ctx) {
    this.ctx ??= ctx;
    if (!this.sites.length && !this._placeSites(ctx)) return;
    this.elapsed += dt;

    if (this.phase === 'over') {
      this.intermission -= dt;
      if (this.intermission <= 0) this._startRound(ctx);
      return;
    }

    if (this.bomb) {
      this.bomb.timer -= dt;
      this._stepDefuse(this.bomb.site, dt);
      if (this.bomb.timer <= 0) this._endRound(this.attackers, 'detonated');
    } else {
      this.roundClock -= dt;
      for (const s of this.sites) this._stepPlant(s, dt);
      if (this.roundClock <= 0) this._endRound(this.defenders, 'time');
    }

    this._checkWipe();
    this._publishZones();
    this._publish();
  }

  _stepPlant(site, dt) {
    const n = this._countInside(site);
    const planting = n[this.attackers];
    if (!planting) {
      // Progress decays rather than resetting: stepping out of the circle for a
      // second to shoot someone should cost you time, not the whole plant.
      site.progress = Math.max(0, site.progress - dt / TUNING.plantTime);
      return;
    }
    site.progress += (dt / TUNING.plantTime) * Math.min(2, planting);
    if (site.progress >= 1) {
      site.progress = 1;
      site.planted = true;
      this.bomb = { site, timer: TUNING.fuse };
      for (const s of this.sites) if (s !== site) s.progress = 0;
      this.events?.emit('objective:captured', {
        zone: site.id, team: this.attackers, position: site.position.clone(),
      });
      const ours = this.attackers === this._localTeam();
      this.events?.emit('ui:banner', {
        key: ours ? 'banner.bombPlanted' : 'banner.bombEnemy',
        params: { zone: site.id },
        tone: ours ? 'good' : 'bad',
      });
    }
  }

  _stepDefuse(site, dt) {
    const n = this._countInside(site);
    const defusing = n[this.defenders];
    if (!defusing) {
      site.defuse = Math.max(0, (site.defuse ?? 0) - dt / TUNING.defuseTime);
      return;
    }
    site.defuse = (site.defuse ?? 0) + (dt / TUNING.defuseTime) * Math.min(2, defusing);
    if (site.defuse >= 1) this._endRound(this.defenders, 'defused');
  }

  _checkWipe() {
    if (this.phase !== 'live') return;
    let a = 0;
    let b = 0;
    for (const reg of this.match.actors.values()) {
      if (reg.dead || typeof reg.actor.health !== 'number') continue;
      if (reg.team === TEAM.ALPHA) a++;
      else if (reg.team === TEAM.BRAVO) b++;
    }
    // A planted bomb outlives the team that planted it — wiping the attackers
    // after the plant does not save you, you still have to defuse.
    if (a === 0 && !(this.bomb && this.attackers === TEAM.ALPHA)) this._endRound(TEAM.BRAVO, 'wipe');
    else if (b === 0 && !(this.bomb && this.attackers === TEAM.BRAVO)) this._endRound(TEAM.ALPHA, 'wipe');
  }

  /* -------------------------------- rounds -------------------------------- */

  _endRound(team, reason) {
    if (this.phase === 'over') return;
    this.phase = 'over';
    this.intermission = TUNING.intermission;
    this.score[team]++;
    this._lastResult = { team, reason };

    const mine = team === this._localTeam();
    this.events?.emit('ui:banner', {
      key: mine ? 'banner.roundWon' : 'banner.roundLost',
      tone: mine ? 'good' : 'bad',
    });
    this.events?.emit('match:round', {
      round: this.round, winner: team, reason, score: { ...this.score },
    });
    this._publish();
  }

  _startRound(ctx) {
    this.round++;
    this.phase = 'live';
    this.roundClock = TUNING.roundTime;
    this.bomb = null;
    for (const s of this.sites) {
      s.progress = 0;
      s.planted = false;
      s.defuse = 0;
    }

    // Halfway through the maximum number of rounds, the sides trade. Computed from
    // the round number rather than toggled on a flag so a mid-match reload — or a
    // future spectator joining late — lands on the same answer.
    const half = this.roundsToWin;
    this.attackers = this.round > half ? TEAM.BRAVO : TEAM.ALPHA;

    const p = ctx.player;
    if (p) {
      const spawn = this.pickSpawn(this.match.get(p), ctx);
      p.respawn(spawn ? spawn.clone() : p.position.clone());
    }
    ctx.ai?.resetRound?.();

    this.events?.emit('ui:banner', {
      key: this.attackers === this._localTeam() ? 'banner.attack' : 'banner.defend',
      tone: 'info',
    });
    this.events?.emit('game:state', { state: 'playing' });
    this._publish();
    this._publishZones();
  }

  _localTeam() {
    return this.match?.teamOf(this.ctx?.player) ?? TEAM.ALPHA;
  }

  pickSpawn(reg, ctx) {
    const spawns = ctx.ai?.spawnPoints;
    if (!spawns?.length || !reg || !this.sites.length) return null;
    // Defenders start on the sites, attackers as far from them as the map allows.
    const defending = reg.team === this.defenders;
    let best = null;
    let bestScore = -Infinity;
    for (const s of spawns) {
      let nearest = Infinity;
      for (const site of this.sites) nearest = Math.min(nearest, s.distanceTo(site.position));
      const score = defending ? -nearest : nearest;
      if (score > bestScore) {
        bestScore = score;
        best = s;
      }
    }
    return best;
  }

  checkWin() {
    for (const team of [TEAM.ALPHA, TEAM.BRAVO]) {
      if (this.score[team] >= this.roundsToWin) {
        const win = team === this._localTeam();
        return { team, reason: 'rounds', win, key: win ? 'banner.victory' : 'banner.defeat' };
      }
    }
    return null;
  }

  _publish() {
    this.events?.emit('match:score', {
      mode: this.id,
      score: { ...this.score },
      limit: this.roundsToWin,
      elapsed: 0,
      timeLimit: 0,
      round: this.round,
      phase: this.phase,
      attackers: this.attackers,
      // The clock the player actually needs: the fuse once it is running, the
      // round timer before that.
      clock: this.bomb ? this.bomb.timer : this.roundClock,
      bomb: this.bomb ? this.bomb.site.id : null,
    });
  }

  _publishZones() {
    this.events?.emit('objective:zones', {
      zones: this.sites.map((s) => ({
        id: s.id,
        position: s.position,
        owner: s.planted ? this.attackers : TEAM.NONE,
        progress: s.planted ? (s.defuse ?? 0) : s.progress,
        contested: s.actors[TEAM.ALPHA] > 0 && s.actors[TEAM.BRAVO] > 0,
        capturing: s.planted ? this.defenders : s.progress > 0 ? this.attackers : null,
        radius: TUNING.radius,
      })),
    });
  }
}

/** Stable small hash, so a given soldier always picks the same site. */
function hashId(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h;
}
